import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip, CircularProgress, TextField, Button, Divider, Typography, Card, CardContent, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AddCircle, ChevronLeft, ChevronRight, Delete, CloudUpload } from "@mui/icons-material";
import Datagrid from "../Datagrid/Datagrid";
import { LABELS, TABLE_HEADERS, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import Swal from "sweetalert2";
import { useSite } from "../../context/siteContext";
import { 
    getMachines, 
    uploadMachineAssets, 
    updateMachine, 
    createMachine, 
    deleteMachines 
} from "../../services/manageMachinesMasterService";
const ManageMachinesMaster = () => {
    const normalizeDateValue = (value) => {
        if (!value) return "1900-01-01";
        const raw = String(value);
        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) return "1900-01-01";
        return parsed.toISOString().slice(0, 10);
    };
    const [allMachines, setAllMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selection, setSelection] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formAlert, setFormAlert] = useState(null);
    const [form, setForm] = useState({
        machine_number: "",
        machine_name: "",
        machine_description: "",
        machine_sell_end_date: "",
        machine_sell_price: "",
    });
    const [files, setFiles] = useState({ image: null });
    const { t } = useTranslation();
    const { fetchSites } = useSite();

    useEffect(() => {
        const fetchAllMachines = async () => {
            try {
                const response = await getMachines();
                setAllMachines(response.data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAllMachines();
    }, []);

    const handleEdit = (row) => {
        setEditRow(row);
        setForm({
            machine_number: row.machine_number || "",
            machine_name: row.machine_name || "",
            machine_description: row.machine_description || "",
            machine_sell_end_date: normalizeDateValue(row.machine_sell_end_date),
            machine_sell_price: row.machine_sell_price ?? "",
        });
        setFiles({ image: null });
        setFormAlert(null);
        setDialogOpen(true);
    };

    const handleAdd = () => {
        setEditRow(null);
        setForm({
            machine_number: "",
            machine_name: "",
            machine_description: "",
            machine_sell_end_date: "",
            machine_sell_price: "",
        });
        setFiles({ image: null });
        setFormAlert(null);
        setDialogOpen(true);
    };

    const refresh = async () => {
        const response = await getMachines();
        setAllMachines(response.data);
    };

    const uploadAssets = async (machineNumber) => {
        if (!files.image) return;
        const fd = new FormData();
        fd.append('machine_number', machineNumber);
        fd.append('image', files.image);
        await uploadMachineAssets(fd);
    };

    const handleSave = async () => {
        try {
            if (!form.machine_number || !form.machine_name) {
                setFormAlert({ severity: "warning", message: t('GENERAL.MACHINE_NUMBER_AND_NAME_ARE_REQUIRED') });
                Swal.fire({
                    icon: "warning",
                    title: t('ALERT_MESSAGES.WARNING'),
                    text: t('GENERAL.MACHINE_NUMBER_AND_NAME_ARE_REQUIRED'),
                });
                return;
            }
            const nextNumber = `${form.machine_number || ""}`.trim().toLowerCase();
            const hasDuplicate = allMachines.some((machine) => {
                if (!machine?.machine_number) return false;
                if (editRow && machine.machine_id === editRow.machine_id) return false;
                return String(machine.machine_number).trim().toLowerCase() === nextNumber;
            });
            if (hasDuplicate) {
                setFormAlert({ severity: "error", message: t('GENERAL.MACHINE_NUMBER_ALREADY_EXISTS') });
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: t('GENERAL.MACHINE_NUMBER_ALREADY_EXISTS'),
                });
                return;
            }
            setIsSubmitting(true);
            const payload = {
                ...form,
                machine_search_name: form.machine_name,
            };
            if (editRow) {
                await updateMachine(editRow.machine_id, payload);
                await uploadAssets(form.machine_number);
            } else {
                await createMachine(payload);
                await uploadAssets(form.machine_number);
            }
            await refresh();
            await fetchSites();
            setDialogOpen(false);
            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: editRow ? t('GENERAL.MACHINE_UPDATED_SUCCESSFULLY') : t('GENERAL.MACHINE_CREATED_SUCCESSFULLY'),
            });
        } catch (e) {
            console.error(e);
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('GENERAL.FAILED_TO_SAVE_MACHINE'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (selection.length === 0) return;
        try {
            const confirmation = await Swal.fire({
                title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                text: t('GENERAL.THIS_WILL_DELETE_THE_MACHINES_PERMANENTLY'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: t('GENERAL.YES_DELETE_IT'),
            });
            if (!confirmation.isConfirmed) return;
            await deleteMachines(selection);
            await refresh();
            await fetchSites();
            setSelection([]);
            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('GENERAL.MACHINES_DELETED_SUCCESSFULLY'),
            });
        } catch (e) { console.error(e); }
    };

    const columns = [
        { field: "machine_number", headerName: t('TABLE_HEADERS.MACHINE_NUMBER'), width: 200 },
        { field: "machine_name", headerName: t('TABLE_HEADERS.MACHINE_NAME'), flex: 1 },
        { field: "machine_description", headerName: t('TABLE_HEADERS.DESCRIPTION'), flex: 1 },
        // { field: "machine_sell_price", headerName: "Sell Price", width: 140 },
        {
            headerName: t('TABLE_HEADERS.ACTIONS'),
            sortable: false,
            width: 100, // Ensure there's enough space for icons
            headerAlign: "center",
            align: "left",
            renderCell: (params) => (
                <div>
                    <Tooltip title={t('GENERAL.EDIT_MACHINE')}>
                        <IconButton
                            color="primary"
                            aria-label="Edit Machine"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleEdit(params.row);
                            }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const customButtons = [
        { label: 'Add', icon: <AddCircle />, onClick: handleAdd },
        { label: t('LABELS.DELETE_SELECTED'), icon: <Delete />, onClick: handleDelete },
    ];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Datagrid
                loading={loading}
                data={allMachines}
                columns={columns}
                customButtons={customButtons}
                checkboxSelection={true}
                rowIdField="machine_id"
                onSelect={setSelection}
                sortBy={[{ field: "machine_id", sort: "asc" }]}
                hideFilterFromColumnMenu
                lockActiveColumnInManageColumns
                preventHidingLastColumn
            />
            <ModalWrapper
                open={dialogOpen}
                setOpen={setDialogOpen}
                title={editRow ? t('GENERAL.EDIT_MACHINE') : t('GENERAL.ADD_MACHINE')}
                content={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {formAlert && (
                            <Alert severity={formAlert.severity || "error"}>{formAlert.message}</Alert>
                        )}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>{t('UI_TEXT.MACHINE_DETAILS')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('GENERAL.PROVIDE_THE_CORE_MACHINE_INFO_USED_FOR_SEARCH_AND_DISPLAY')}
                            </Typography>
                        </Box>
                        <TextField label={t('TABLE_HEADERS.MACHINE_NUMBER')} value={form.machine_number} onChange={e => setForm({ ...form, machine_number: e.target.value })} size="small" />
                        <TextField label={t('TABLE_HEADERS.MACHINE_NAME')} value={form.machine_name} onChange={e => setForm({ ...form, machine_name: e.target.value })} size="small" />
                        <TextField label={t('TABLE_HEADERS.DESCRIPTION')} value={form.machine_description} onChange={e => setForm({ ...form, machine_description: e.target.value })} multiline minRows={3} size="small" />
                        <TextField
                            label= {t('GENERAL.SELL_END_DATE')}
                            value={form.machine_sell_end_date}
                            onChange={e => setForm({ ...form, machine_sell_end_date: e.target.value })}
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ placeholder: "" }}
                        />
                        {/* <TextField label={t('GENERAL.SELL_PRICE')} value={form.machine_sell_price} onChange={e => setForm({ ...form, machine_sell_price: e.target.value })} type="number" size="small" /> */}
                        <Divider />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>{t('GENERAL.MACHINE_IMAGE')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('GENERAL.UPLOAD_A_PNG_IMAGE_FOR_MACHINE_CARDS_AND_LISTINGS')}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                borderRadius: "10px",
                                background: "#eee",
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                width: "100%",
                                columnGap: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<CloudUpload />}
                                disabled={files.image ? true : false}
                                onClick={() => document.getElementById("machineImageInput").click()}
                            >
                                 {t('FILE_UPLOAD.CHOOSE_FILE')}
                            </Button>
                            <Typography variant="body2" fontSize="11px">
                                {files.image ? files.image?.name : t('GENERAL.ATTACH_A_MACHINE_IMAGE_PNG')}
                            </Typography>
                        </Box>
                        <input
                            type="file"
                            id="machineImageInput"
                            accept=".png"
                            style={{ display: "none" }}
                            onChange={e => setFiles(prev => ({ ...prev, image: e.target.files?.[0] || null }))}
                        />
                        {files.image && (
                            <Card
                                variant="outlined"
                                sx={{
                                    width: "100%",
                                    "&:hover": { backgroundColor: "action.hover" },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        py: 1.5,
                                        "&:last-child": { pb: 1.5 },
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" gap={0.25}>
                                        <Typography variant="body2">{files.image?.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {files.image?.size ? `${(files.image.size / 1024).toFixed(1)} KB` : ""}
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => setFiles(prev => ({ ...prev, image: null }))}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                }
                actions={
                    <>
                        <Button onClick={() => setDialogOpen(false)} disabled={isSubmitting}>{t('BUTTON.CANCEL')}</Button>
                        <Button variant="contained" onClick={handleSave} disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                                    {t('BUTTON.SUBMITTING')}
                                </>
                            ) : (
                                t('BUTTON.SAVE')
                            )}
                        </Button>
                    </>
                }
            />
        </div>
    );
};

export default ManageMachinesMaster;